import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { InputNumber } from 'primereact/inputnumber';
import { FinancialDebtSideBarFilterDatamodel } from './financial-debt-side-bar-filter-datamodel';
import { Button } from 'primereact/button';
import { ProgramStatus } from '../../../../../api/types/programs/program-status';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface FinancialDebtSideBarFilterComponentMetamodel {
  onSearch: (filters: FinancialDebtSideBarFilterDatamodel) => any;
}

export const FinancialDebtSideBarFilter: React.FC<FinancialDebtSideBarFilterComponentMetamodel> =
  observer(({ onSearch }) => {
    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<ProgramStatus[]>(
      [],
    );
    const [minDebtAmount, setMinDebtAmount] = useState<number | null>(null);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranches((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleProgramToggle = (programId: number) => {
      setSelectedPrograms((prev) =>
        prev.includes(programId)
          ? prev.filter((p) => p !== programId)
          : [...prev, programId],
      );
    };

    const handleYearToggle = (yearStr: string) => {
      const year = parseInt(yearStr);
      setSelectedYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleStatusToggle = (status: ProgramStatus) => {
      setSelectedStatuses((prev) =>
        prev.includes(status)
          ? prev.filter((s) => s !== status)
          : [...prev, status],
      );
    };

    const handleSearch = () => {
      const filters: FinancialDebtSideBarFilterDatamodel = {
        branchesIds: selectedBranches,
        abstractProgramIds: selectedPrograms,
        startYears: selectedYears,
        programsStatuses: selectedStatuses,
        minDebtAmount: minDebtAmount,
      };
      onSearch(filters);
    };

    const handleClearAllFilters = () => {
      setSelectedBranches([]);
      setSelectedPrograms([]);
      setSelectedYears([]);
      setSelectedStatuses([]);
      setMinDebtAmount(null);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#333',
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      debtAmountContainer: {
        padding: '8px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      inputLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.header}>סינונים</div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="תכניות לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedPrograms}
            onItemToggle={handleProgramToggle}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תכנית..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={handleYearToggle}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס תוכנית"
            items={Object.values(ProgramStatus).filter(
              (status) => typeof status === 'string',
            )}
            selectedItems={selectedStatuses}
            onItemToggle={handleStatusToggle}
            getItemLabel={(status) =>
              ProgramStatus.getDisplayText(status as ProgramStatus)
            }
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />

          <CustomExpander>
            <div>סכום חוב</div>
            <div style={styles.debtAmountContainer}>
              <span style={styles.inputLabel}>סכום חוב החל מ:</span>
              <InputNumber
                value={minDebtAmount}
                onValueChange={(e) => setMinDebtAmount(e.value ?? null)}
                mode="currency"
                currency="ILS"
                locale="he-IL"
                placeholder="הזן סכום..."
                style={{ width: '100%' }}
              />
            </div>
          </CustomExpander>
        </div>

        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  });
